import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { AccountService } from '../../services/account.service';
import { CollectionsService } from '../../services/collections.service';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { PERMISSIONS } from '../../constants/permissions';
import {
  Activity, ActivityType, CollectionsSettings, CreditControlMode, CustomerCredit, WorklistBucket, WorklistRow,
} from '../../interfaces/sales/collections.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import FocusedRowChip from '../common/FocusedRowChip';
import { useFocusedRow } from '../../hooks/useFocusedRow';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { leafAccounts } from '../../utils/postingAccounts';

const BUCKETS: WorklistBucket[] = ['ALL', 'OVERDUE', 'CURRENT', '1-30', '31-60', '61-90', '90+'];
const ACTIVITY_TYPES: ActivityType[] = ['CALL', 'EMAIL', 'VISIT', 'NOTE'];
const filterCell = { flex: '1 1 200px', maxWidth: 280 };

/** W3-N6: the collections worklist, one row per customer with money outstanding. */
const CollectionsWorklist: React.FC = () => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canManage = hasPermission(PERMISSIONS.RECEIPT_MANAGE);
  const canCredit = hasPermission(PERMISSIONS.CUSTOMER_CREDIT_MANAGE);
  const canSettings = hasPermission(PERMISSIONS.SALES_SETTINGS_MANAGE);

  const [rows, setRows] = useState<WorklistRow[]>([]);
  const { focusId: focusCustomerId, clear: clearFocus } = useFocusedRow('customerId');
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState<WorklistBucket>('ALL');
  const [holdOnly, setHoldOnly] = useState(false);
  const [followUp, setFollowUp] = useState(false);
  const [activityFor, setActivityFor] = useState<WorklistRow | null>(null);
  const [creditFor, setCreditFor] = useState<WorklistRow | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await CollectionsService.worklist(institutionId, {
        bucket: bucket === 'ALL' ? undefined : bucket, hold: holdOnly || undefined, followUp: followUp || undefined,
      }));
    } catch (e) {
      toast.showApiError(e, 'Failed to load the collections worklist');
    } finally {
      setLoading(false);
    }
  }, [institutionId, bucket, holdOnly, followUp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.collections.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Collections</Typography>
        {canSettings && <Button data-help="sales.collections.settings" variant="outlined" onClick={() => setSettingsOpen(true)}>Credit control settings</Button>}
      </Box>
      <FocusedRowChip focusId={focusCustomerId} noun="customer" onClear={clearFocus} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }} data-help-section="sales.collections.filters">
        <TextField data-help="sales.collections.bucket" select size="small" label="Overdue bucket" value={bucket} sx={filterCell}
          onChange={e => setBucket(e.target.value as WorklistBucket)}>
          {BUCKETS.map(b => <MenuItem key={b} value={b}>{b === 'ALL' ? 'All customers' : b}</MenuItem>)}
        </TextField>
        <FormControlLabel data-help="sales.collections.holdOnly" control={<Switch checked={holdOnly} onChange={e => setHoldOnly(e.target.checked)} />} label="On credit hold" />
        <FormControlLabel data-help="sales.collections.followUp" control={<Switch checked={followUp} onChange={e => setFollowUp(e.target.checked)} />} label="Bounced cheque follow-up" />
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.collections.table">
          <Table size="small" data-testid="worklist-table">
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.collections.col.customer">Customer</TableCell>
                <TableCell align="right" data-help="sales.collections.col.outstanding">Outstanding</TableCell>
                <TableCell align="right" data-help="sales.collections.col.overdue">Overdue</TableCell>
                <TableCell align="right" data-help="sales.collections.col.b90plus">90+</TableCell>
                <TableCell align="right" data-help="sales.collections.col.creditLimit">Credit limit</TableCell>
                <TableCell data-help="sales.collections.col.promise">Promise</TableCell>
                <TableCell data-help="sales.collections.col.lastContact">Last contact</TableCell>
                <TableCell data-help="sales.collections.col.flags">Flags</TableCell>
                <TableCell data-help="sales.collections.col.actions" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={9} align="center">Nothing to chase.</TableCell></TableRow>}
              {(focusCustomerId ? rows.filter(r => r.customerId === focusCustomerId) : rows).map(r => (
                <TableRow key={r.customerId} data-testid={`worklist-row-${r.customerId}`}>
                  <TableCell>{r.customerName}</TableCell>
                  <TableCell align="right">{formatMoney(r.outstanding)}</TableCell>
                  <TableCell align="right">{formatMoney(r.overdue)}</TableCell>
                  <TableCell align="right">{formatMoney(r.bucket90Plus)}</TableCell>
                  <TableCell align="right">{r.creditLimit != null ? formatMoney(r.creditLimit) : '-'}</TableCell>
                  <TableCell>
                    {r.promiseDate ? `${formatMoney(r.promiseAmount ?? 0)} by ${formatDate(r.promiseDate)} (${humanizeEnum(r.promiseStatus ?? '')})` : '-'}
                  </TableCell>
                  <TableCell>{r.lastActivityAt ? `${humanizeEnum(r.lastActivityType ?? '')} ${formatDate(r.lastActivityAt)}` : '-'}</TableCell>
                  <TableCell>
                    {r.creditHold && (
                      <Tooltip title={r.creditHoldReason || 'No reason recorded'}>
                        <Chip size="small" color="error" label={r.creditHoldReason ? `Credit hold: ${r.creditHoldReason}` : 'Credit hold'}
                          sx={{ mr: 0.5, maxWidth: 260 }} />
                      </Tooltip>
                    )}
                    {r.overLimit && <Chip size="small" color="warning" label="Over limit" sx={{ mr: 0.5 }} />}
                    {r.bouncedChequeFollowUp && <Chip size="small" color="warning" label="Bounced cheque" sx={{ mr: 0.5 }} />}
                    {r.lastDunningLevel != null && <Chip size="small" label={`Dunning L${r.lastDunningLevel}`} />}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button data-help="sales.collections.action.log" size="small" onClick={() => setActivityFor(r)}>Log</Button>
                    {canCredit && <Button data-help="sales.collections.action.credit" size="small" onClick={() => setCreditFor(r)}>Credit</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {activityFor && (
        <ActivityDialog row={activityFor} canManage={canManage} onClose={() => { setActivityFor(null); load(); }} />
      )}
      {creditFor && <CreditDialog row={creditFor} onClose={() => setCreditFor(null)} onSaved={() => { setCreditFor(null); load(); }} />}
      {settingsOpen && <SettingsDialog institutionId={institutionId} onClose={() => setSettingsOpen(false)} />}
    </Box>
  );
};

const ActivityDialog: React.FC<{ row: WorklistRow; canManage: boolean; onClose: () => void }> = ({ row, canManage, onClose }) => {
  const toast = useToast();
  const [items, setItems] = useState<Activity[]>([]);
  const [type, setType] = useState<ActivityType>('CALL');
  const [note, setNote] = useState('');
  const [promiseDate, setPromiseDate] = useState('');
  const [promiseAmount, setPromiseAmount] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [openInvoices, setOpenInvoices] = useState<{ id: string; label: string; outstanding: number }[]>([]);
  const [saving, setSaving] = useState(false);

  // The customer's open invoices: a promise tied to one feeds the cash forecast's "promises to pay" row.
  useEffect(() => {
    if (!canManage) return;
    SalesInvoiceService.listByCustomer(row.customerId)
      .then(list => setOpenInvoices((list ?? [])
        .filter(i => i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID')
        .map(i => ({
          id: i.id, outstanding: i.totalAmount - (i.paidAmount ?? 0) - (i.creditedAmount ?? 0),
          label: i.invoiceNumber ?? i.id,
        }))
        .filter(i => i.outstanding > 0.004)))
      .catch(() => setOpenInvoices([]));
  }, [row.customerId, canManage]);

  const reload = useCallback(() => {
    CollectionsService.listActivity(row.customerId).then(setItems)
      .catch(e => toast.showApiError(e, 'Failed to load the collection log'));
  }, [row.customerId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    try {
      setSaving(true);
      await CollectionsService.addActivity({
        customerId: row.customerId, type, note: note.trim() || undefined,
        salesInvoiceId: invoiceId || undefined,
        promiseDate: promiseDate || undefined, promiseAmount: promiseAmount ? Number(promiseAmount) : undefined,
      });
      toast.showSuccess('Logged');
      setNote(''); setPromiseDate(''); setPromiseAmount(''); setInvoiceId('');
      reload();
    } catch (e) {
      toast.showApiError(e, 'Failed to log the contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth data-help-page="sales.collections.logDialog.page">
      <DialogTitle>Collection log - {row.customerName}</DialogTitle>
      <DialogContent data-help-section="sales.collections.logDialog.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {canManage && (
          <>
            <TextField data-help="sales.collections.log.type" select label="Contact type" value={type} fullWidth onChange={e => setType(e.target.value as ActivityType)}>
              {ACTIVITY_TYPES.map(t => <MenuItem key={t} value={t}>{humanizeEnum(t)}</MenuItem>)}
            </TextField>
            <TextField data-help="sales.collections.log.note" label="Note" value={note} multiline minRows={2} fullWidth onChange={e => setNote(e.target.value)} />
            <TextField data-help="sales.collections.log.promiseDate" label="Promise to pay by (optional)" type="date" value={promiseDate} fullWidth
              InputLabelProps={{ shrink: true }} onChange={e => setPromiseDate(e.target.value)} />
            <TextField data-help="sales.collections.log.promiseAmount" label="Promised amount" type="number" value={promiseAmount} fullWidth
              helperText="Both promise fields are needed for a promise." onChange={e => setPromiseAmount(e.target.value)} />
            <TextField data-help="sales.collections.log.invoice" select label="Invoice (optional)" value={invoiceId} fullWidth
              helperText="Tie a promise to an invoice so it counts in the cash forecast."
              onChange={e => setInvoiceId(e.target.value)}>
              <MenuItem value="">None</MenuItem>
              {openInvoices.map(i => (
                <MenuItem key={i.id} value={i.id}>{i.label} (outstanding {formatMoney(i.outstanding)})</MenuItem>
              ))}
            </TextField>
            <Button data-help="sales.collections.log.add" variant="contained" disabled={saving} onClick={save}>Add entry</Button>
          </>
        )}
        {items.map(a => (
          <Paper key={a.id} variant="outlined" sx={{ p: 1 }}>
            <Typography variant="body2">
              {formatDate(a.occurredOn)} - {humanizeEnum(a.type)}{a.note ? `: ${a.note}` : ''}
              {a.promiseDate ? ` (promise ${formatMoney(a.promiseAmount ?? 0)} by ${formatDate(a.promiseDate)}, ${humanizeEnum(a.promiseStatus ?? '')})` : ''}
            </Typography>
          </Paper>
        ))}
      </DialogContent>
      <DialogActions><Button data-help="sales.collections.log.close" onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

const CreditDialog: React.FC<{ row: WorklistRow; onClose: () => void; onSaved: () => void }> = ({ row, onClose, onSaved }) => {
  const toast = useToast();
  const [credit, setCredit] = useState<CustomerCredit | null>(null);
  const [limit, setLimit] = useState('');
  const [terms, setTerms] = useState('');
  const [hold, setHold] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    CollectionsService.getCredit(row.customerId).then(c => {
      setCredit(c);
      setLimit(c.creditLimit != null ? String(c.creditLimit) : '');
      setTerms(c.paymentTermsDays != null ? String(c.paymentTermsDays) : '');
      setHold(c.creditHold);
      setReason(c.creditHoldReason ?? '');
    }).catch(e => toast.showApiError(e, 'Failed to load credit terms'));
  }, [row.customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      await CollectionsService.saveCredit(row.customerId, {
        creditLimit: limit === '' ? null : Number(limit), paymentTermsDays: terms === '' ? null : Number(terms),
        creditHold: hold, creditHoldReason: hold ? reason : null,
      });
      toast.showSuccess('Credit terms saved');
      onSaved();
    } catch (e) {
      toast.showApiError(e, 'Failed to save credit terms');
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth data-help-page="sales.collections.creditDialog.page">
      <DialogTitle>Credit terms - {row.customerName}</DialogTitle>
      <DialogContent data-help-section="sales.collections.creditDialog.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {credit && (
          <Alert severity="info">
            Exposure {formatMoney(credit.exposure)} (open invoices {formatMoney(credit.openInvoices)} less unapplied credit {formatMoney(credit.unappliedCredits)}).
            Credit control is {credit.mode}.
          </Alert>
        )}
        <TextField data-help="sales.collections.credit.limit" label="Credit limit" type="number" value={limit} fullWidth helperText="Blank = no limit" onChange={e => setLimit(e.target.value)} />
        <TextField data-help="sales.collections.credit.terms" label="Payment terms (days)" type="number" value={terms} fullWidth helperText="Blank = use the institution default" onChange={e => setTerms(e.target.value)} />
        <FormControlLabel data-help="sales.collections.credit.hold" control={<Switch checked={hold} onChange={e => setHold(e.target.checked)} />} label="Credit hold" />
        {hold && <TextField data-help="sales.collections.credit.reason" label="Reason" value={reason} required fullWidth onChange={e => setReason(e.target.value)} />}
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.collections.credit.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="sales.collections.credit.save" variant="contained" disabled={hold && !reason.trim()} onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const SettingsDialog: React.FC<{ institutionId: string; onClose: () => void }> = ({ institutionId, onClose }) => {
  const toast = useToast();
  const [s, setS] = useState<CollectionsSettings>({ institutionId, creditControlMode: 'OFF' });
  const [accounts, setAccounts] = useState<Account[]>([]);

  useEffect(() => {
    CollectionsService.getSettings(institutionId).then(setS)
      .catch(e => toast.showApiError(e, 'Failed to load settings'));
    AccountService.getAllAccounts().then(a => setAccounts(leafAccounts(a)))
      .catch(e => toast.showApiError(e, 'Failed to load accounts'));
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      await CollectionsService.saveSettings(s);
      toast.showSuccess('Settings saved');
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to save settings');
    }
  };
  const assets = accounts.filter(a => a.accountType === 'ASSET');
  const expenses = accounts.filter(a => a.accountType === 'EXPENSE');

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth data-help-page="sales.collections.settingsDialog.page">
      <DialogTitle>Credit control and remittance accounts</DialogTitle>
      <DialogContent data-help-section="sales.collections.settingsDialog.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField data-help="sales.collections.settingsDialog.mode" select label="Credit control on invoice issue" value={s.creditControlMode} fullWidth
          onChange={e => setS({ ...s, creditControlMode: e.target.value as CreditControlMode })}>
          <MenuItem value="OFF">Off</MenuItem>
          <MenuItem value="WARN">Warn</MenuItem>
          <MenuItem value="BLOCK">Block</MenuItem>
        </TextField>
        <AccountAutocomplete data-help="sales.collections.settingsDialog.tds" accounts={assets} value={s.tdsReceivableAccountId ?? ''} label="TDS receivable account"
          onChange={id => setS({ ...s, tdsReceivableAccountId: id || null })} />
        <AccountAutocomplete data-help="sales.collections.settingsDialog.vds" accounts={assets} value={s.vdsCreditAccountId ?? ''} label="VDS credit account"
          onChange={id => setS({ ...s, vdsCreditAccountId: id || null })} />
        <AccountAutocomplete data-help="sales.collections.settingsDialog.bankCharge" accounts={expenses} value={s.bankChargeAccountId ?? ''} label="Bank charge account"
          onChange={id => setS({ ...s, bankChargeAccountId: id || null })} />
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.collections.settingsDialog.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="sales.collections.settingsDialog.save" variant="contained" onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectionsWorklist;
